/** Google's supported Preferred Sources SDK owns the popup and completion flow. */
export interface GooglePreferredSource {
  init(options: { theme: 'light' }): void
  addPreferredSource(): void
}

type PreferredSourceQueue = { push(callback: (client: GooglePreferredSource) => void): unknown }
type PublisherWindow = Window & { PREFERRED_SOURCE?: PreferredSourceQueue }
let client: GooglePreferredSource | null = null
let loading: Promise<GooglePreferredSource> | null = null

export function getGooglePreferredSource(): GooglePreferredSource | null {
  return client
}

/**
 * Warm the SDK on reader INTENT (pointer enters the button, it takes focus, a
 * finger lands on it) — never on page mount. Google's `publisher.js` frames
 * news.google.com as soon as it runs, and that frame's own report-only
 * `frame-ancestors 'self'` policy logs a Content Security Policy message in
 * the console of every page that mounted the button. It is Google's policy
 * about Google's frame, not ours (our frame-src allows news.google.com), but
 * the axe gate rightly treats any CSP message during load as a template
 * fault, and it turned `main` red on 09/08/2026 and PR #765 on 09/11 with the
 * button doing nothing but sitting in the footer. Loading on intent keeps a
 * third-party script off every page load, and the click path is unchanged:
 * the SDK opens its dialog when it is there, and the synchronous popup
 * fallback in preferred-source-link.tsx opens Google's documented deeplink
 * when it is not — inside the click, so no popup blocker is involved.
 */
export function warmGooglePreferredSource(): void {
  void loadGooglePreferredSource().catch(() => {})
}

export function loadGooglePreferredSource(): Promise<GooglePreferredSource> {
  if (client) return Promise.resolve(client)
  if (loading) return loading
  loading = new Promise((resolve, reject) => {
    const win = window as PublisherWindow
    win.PREFERRED_SOURCE = win.PREFERRED_SOURCE || []
    win.PREFERRED_SOURCE.push((sdk) => {
      sdk.init({ theme: 'light' })
      client = sdk
      resolve(sdk)
    })
    if (!document.getElementById('google-preferred-source-sdk')) {
      const script = document.createElement('script')
      script.id = 'google-preferred-source-sdk'
      script.async = true
      script.src = 'https://news.google.com/swg/js/v1/publisher.js'
      script.setAttribute('preferred-sources-control', 'manual')
      script.onerror = () => { script.remove(); loading = null; reject(new Error('Google Preferred Sources could not load')) }
      document.head.appendChild(script)
    }
  })
  return loading
}
